import { Link } from 'react-router';
import {
  Search,
  PenTool,
  Mail,
  ArrowRight,
  CheckCircle,
  X,
  Chrome,
  Bot,
  Code2,
} from 'lucide-react';
import { StickyNav } from '@/components/landing/StickyNav';
import { AmbientOrbs } from '@/components/landing/AmbientOrbs';
import { FadeUp } from '@/components/landing/FadeUp';
// ProductPreview replaced by EmailComparison in hero
import { ScrollProgress } from '@/components/landing/ScrollProgress';
import { VersionToggle } from '@/components/landing/VersionToggle';
import {
  SlideInLeft,
  SlideInRight,
  ScaleUp,
  BlurIn,
  StaggerChildren,
  CountUp,
} from '@/components/landing/RevealAnimations';
import { ExtensionPreview } from '@/components/landing/ExtensionPreview';
import { EmailComparison } from '@/components/landing/EmailComparison';
import { MetricsBar } from '@/components/landing/MetricsBar';

/* ── placeholder ── */
const PLACEHOLDER_CHROME_STORE_URL = 'https://chromewebstore.google.com';

/* ── Data ── */

/*
 * STEP ICON IMAGE GENERATION PROMPTS (for Nano Banana 2 or similar)
 * Generate each as a 256×256 PNG with transparent background.
 * Place files at: /public/img/step-1.png, step-2.png, step-3.png
 *
 * Step 1 — "Find your leads"
 * Prompt: "Minimal isometric illustration, dark navy background, a glowing
 *   magnifying glass hovering over a grid of profile cards with small company
 *   logos. One card is highlighted with a subtle electric-violet pulse ring
 *   indicating 'found'. Clean vector style, SaaS product illustration,
 *   no text, soft gradient shadows, professional B2B feel. 256x256."
 *
 * Step 2 — "AI writes every email"
 * Prompt: "Minimal isometric illustration, dark navy background, an AI brain
 *   icon connected by light beams to an email draft being written in
 *   real-time — visible lines of text appearing on a floating email card.
 *   A small sparkle/star accent near the text suggests intelligence.
 *   Clean vector style, SaaS product illustration, no readable text,
 *   electric-violet and cyan accent colors, professional B2B feel. 256x256."
 *
 * Step 3 — "Review and send from Gmail"
 * Prompt: "Minimal isometric illustration, dark navy background, a Gmail
 *   envelope icon with a small checkmark badge, sitting in an inbox tray.
 *   A human hand cursor hovers over a 'Send' button. Clean vector style,
 *   SaaS product illustration, no readable text, warm green accent for
 *   the checkmark, professional B2B feel. 256x256."
 */
const steps = [
  {
    icon: Search,
    // Replace with: <img src="/img/step-1.png" alt="" className="w-full h-full object-contain" />
    title: 'Find your leads',
    desc: 'Describe your ideal customer. AI searches the web and returns real people with verified emails.',
  },
  {
    icon: PenTool,
    // Replace with: <img src="/img/step-2.png" alt="" className="w-full h-full object-contain" />
    title: 'AI writes every email',
    desc: 'Each lead gets researched individually. AI writes a unique, relevant email — no templates, no merge tags.',
  },
  {
    icon: Mail,
    // Replace with: <img src="/img/step-3.png" alt="" className="w-full h-full object-contain" />
    title: 'Review and send from Gmail',
    desc: 'Emails appear as drafts in your real inbox. Review, tweak, send. Your domain, your reputation.',
  },
];

const diffRows = [
  { feature: 'Personalization', good: 'AI researches each lead individually', bad: 'Template merge tags ({first_name})' },
  { feature: 'Inbox delivery', good: 'Your Gmail — zero warmup, zero spam risk', bad: 'Weeks of warmup, still hits spam' },
  { feature: 'Email quality', good: 'Every email is unique', bad: 'All look identical' },
  { feature: 'Sending', good: 'Your real Gmail inbox', bad: 'Burner domains + rotating inboxes' },
  { feature: 'Time to results', good: '5 minutes to first campaign', bad: 'Days of setup before sending' },
];

const testimonials = [
  {
    name: 'Daniel K.',
    role: 'Founder',
    company: 'B2B SaaS, Munich',
    kpi: '8 meetings',
    kpiContext: 'from 30 emails',
    quote:
      'I was spending 2 hours a day writing cold emails. Now I review AI-written drafts in my Gmail and hit send. The personalization is better than what I wrote manually.',
    initials: 'DK',
  },
  {
    name: 'Lena W.',
    role: 'Head of BD',
    company: 'Agency, Berlin',
    kpi: '3x replies',
    kpiContext: 'vs. Instantly',
    quote:
      'Switched from Instantly. The emails actually reference real things about each company — not just {first_name} and a template. Replies went from noise to real conversations.',
    initials: 'LW',
  },
  {
    name: 'Marc T.',
    role: 'Solo founder',
    company: 'Dev tools',
    kpi: '0 spam',
    kpiContext: 'issues, ever',
    quote:
      'No warmup, no rotating inboxes, no deliverability anxiety. I send from my real Gmail and every email actually arrives. That alone made me switch.',
    initials: 'MT',
  },
];

const freePlanFeatures = [
  '500 tokens to try a full campaign',
  'AI lead discovery & research',
  'Hyper-personalized email generation',
  'Gmail draft creation via extension',
  'REST API & MCP access',
];

const proPlanFeatures = [
  'Everything in Free',
  '5,000 tokens every month',
  'Unlimited campaigns',
  'Buy more tokens anytime — uncapped',
  'Priority support',
];

const faqs = [
  {
    q: 'Does HyPeMa send emails?',
    a: 'No. HyPeMa creates drafts in your real Gmail inbox. You review each email and click send yourself. Your domain reputation stays clean because the emails come from you.',
  },
  {
    q: 'How is this different from Instantly or Apollo?',
    a: 'Those tools optimize for volume — thousands of emails per day from burner domains that need weeks of warmup. HyPeMa optimizes for replies. AI researches each lead individually, writes a unique email, and you send from your real Gmail. No warmup, no spam risk, no burner domains.',
  },
  {
    q: 'Do I need the Chrome extension?',
    a: 'Yes — it\'s how HyPeMa creates drafts in your Gmail. It only writes drafts. It never reads your existing emails, contacts, or any other data.',
  },
  {
    q: 'How many leads per campaign?',
    a: '10–50. HyPeMa is built for precision — each email is individually researched. You don\'t need 10,000 leads when 40 emails book 8+ meetings.',
  },
  {
    q: 'Can I use HyPeMa with AI assistants?',
    a: 'Yes. HyPeMa has an MCP server that works with Claude, ChatGPT, Cursor, and any MCP-compatible client. You can also build custom workflows with the REST API.',
  },
  {
    q: 'What do tokens cover?',
    a: 'Tokens pay for AI work: ~15 tokens writes one personalized email, ~30 tokens finds and researches one lead. The free plan gives you 500 tokens — enough to run a real campaign and see results.',
  },
  {
    q: 'Is HyPeMa GDPR compliant?',
    a: 'Yes. We process minimal data, never sell or share information, and all data is stored in EU-Frankfurt. See our Privacy Policy for details.',
  },
];

/* ── Page ── */

export function LandingPage() {
  return (
    <VersionToggle>
      <div className="min-h-screen bg-base-100 relative scroll-smooth">
        <ScrollProgress />
        <AmbientOrbs />
        <div className="dot-grid absolute inset-0 pointer-events-none opacity-40" />

        <StickyNav />

        {/* ═══════════════════════════════════════════
            1. HERO — Problem-first, then solution
            ═══════════════════════════════════════════ */}
        <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 pt-10 sm:pt-16 pb-14 sm:pb-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-start">
            <SlideInLeft className="lg:pt-32">
              <div className="space-y-5">
                <h1
                  className="text-4xl sm:text-[2.7rem] lg:text-[3.3rem] font-extrabold tracking-tight leading-[1.08]"
                  style={{ textWrap: 'balance' } as React.CSSProperties}
                >
                  AI runs your outreach.
                  <br />
                  <span className="text-gradient-shimmer">
                    You collect replies.
                  </span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-base-content/55 leading-relaxed font-medium py-4">
                  Every email reads like you spent{' '}
                  <span className="text-base-content/80 font-bold">60&nbsp;minutes</span>{' '}
                  researching the prospect. You didn't — AI did it in{' '}
                  <span className="text-gradient font-bold">6&nbsp;seconds</span>.
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Link
                    to="/onboarding"
                    className="btn btn-primary gap-2 min-h-[44px]"
                  >
                    Send emails that get replies <ArrowRight size={16} />
                  </Link>
                  <a
                    href="#how-it-works"
                    className="btn btn-ghost text-base-content/50 min-h-[44px]"
                  >
                    How it works
                  </a>
                </div>
                <p className="text-xs text-base-content/30 pt-1">
                  500 free tokens. No credit card. No setup.
                </p>
              </div>
            </SlideInLeft>

            <SlideInRight
              delay={300}
              className="w-full"
            >
              <EmailComparison />
            </SlideInRight>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            2. CONTRAST BAR — The before/after, felt
            ═══════════════════════════════════════════ */}
        <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 lg:px-12 pb-14 sm:pb-20">
          <MetricsBar />
        </section>

        {/* Email comparison is now in the hero */}

        {/* ═══════════════════════════════════════════
            4. HOW IT WORKS — Three clear steps
            ═══════════════════════════════════════════ */}
        <section
          id="how-it-works"
          className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20"
        >
          <BlurIn>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center mb-10 sm:mb-12">
              Three steps.{' '}
              <span className="text-primary">Zero spam folders.</span>
            </h2>
          </BlurIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {steps.map((step, i) => (
              <FadeUp key={step.title} delay={i * 120}>
                <div className="glass-card rounded-xl p-5 sm:p-6 text-center space-y-3 h-full hover:scale-[1.02] hover:shadow-xl transition-all duration-300 group">
                  {/* TODO: Replace icon with generated illustration from prompts above */}
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors duration-300">
                    <step.icon
                      size={24}
                      className="text-primary group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="text-xs font-mono-stat text-primary/60 uppercase tracking-widest">
                    Step {i + 1}
                  </div>
                  <h3 className="text-base font-semibold">{step.title}</h3>
                  <p className="text-sm text-base-content/50 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            5. DIFFERENTIATION — One unified section
               Merges anti-patterns + comparison into
               a single coherent argument
            ═══════════════════════════════════════════ */}
        <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20">
          <BlurIn>
            <h2
              className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-center mb-3"
              style={{ textWrap: 'balance' } as React.CSSProperties}
            >
              No templates. No spray-and-pray.{' '}
              <span className="text-gradient">No mass slob blasts.</span>
            </h2>
            <p className="text-center text-sm text-base-content/40 mb-8 sm:mb-10 max-w-lg mx-auto">
              Mass-email tools optimize for volume. HyPeMa optimizes for the only metric that matters — replies.
            </p>
          </BlurIn>

          {/* Desktop: table */}
          <ScaleUp delay={100}>
            <div className="hidden md:block glass-card rounded-xl overflow-hidden">
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-xs text-base-content/40 uppercase tracking-wider font-semibold" />
                    <th className="text-xs uppercase tracking-wider font-semibold text-primary">
                      HyPeMa
                    </th>
                    <th className="text-xs text-base-content/40 uppercase tracking-wider font-semibold">
                      Instantly / Apollo / etc.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {diffRows.map((row) => (
                    <tr
                      key={row.feature}
                      className="border-b border-white/5 last:border-0 hover:bg-base-content/[0.02] transition-colors"
                    >
                      <td className="text-sm text-base-content/50 py-3.5 pr-4 font-medium">
                        {row.feature}
                      </td>
                      <td className="text-sm font-medium py-3.5 pr-4">
                        <span className="flex items-center gap-1.5">
                          <CheckCircle
                            size={14}
                            className="text-success shrink-0"
                          />
                          {row.good}
                        </span>
                      </td>
                      <td className="text-sm text-base-content/35 py-3.5">
                        <span className="flex items-center gap-1.5">
                          <X size={14} className="text-error/40 shrink-0" />
                          {row.bad}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScaleUp>

          {/* Mobile: stacked cards */}
          <StaggerChildren className="md:hidden space-y-3" staggerMs={80}>
            {diffRows.map((row) => (
              <div
                key={row.feature}
                className="glass-card rounded-xl p-4 space-y-2"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-base-content/35">
                  {row.feature}
                </p>
                <div className="flex items-start gap-2">
                  <CheckCircle
                    size={14}
                    className="text-success shrink-0 mt-0.5"
                  />
                  <p className="text-sm font-medium text-base-content/80">
                    {row.good}
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <X size={14} className="text-error/40 shrink-0 mt-0.5" />
                  <p className="text-sm text-base-content/35">{row.bad}</p>
                </div>
              </div>
            ))}
          </StaggerChildren>
        </section>

        {/* ═══════════════════════════════════════════
            6. CHROME EXTENSION — How you actually send
            ═══════════════════════════════════════════ */}
        <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <SlideInLeft>
              <div className="space-y-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary/80">
                  <Chrome size={12} /> Chrome Extension
                </span>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                  Your real Gmail.{' '}
                  <span className="text-gradient">Your reputation.</span>
                </h2>
                <p className="text-sm text-base-content/50 leading-relaxed max-w-md">
                  No burner domains. No warmup pools. No deliverability anxiety.
                  Emails appear as drafts in your inbox — review each one, hit
                  send.
                </p>
                <p className="text-xs text-base-content/35 font-medium">
                  The extension only creates drafts. It never reads your emails,
                  contacts, or any other data.
                </p>
                <a
                  href={PLACEHOLDER_CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-ghost gap-2 border-base-content/15 w-fit min-h-[40px]"
                >
                  <Chrome size={14} />
                  Add to Chrome — free
                </a>
              </div>
            </SlideInLeft>

            <SlideInRight
              delay={200}
              className="flex justify-center lg:justify-end"
            >
              <ExtensionPreview />
            </SlideInRight>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            7. SOCIAL PROOF — Numbers + quotes
            ═══════════════════════════════════════════ */}
        <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20">
          <BlurIn>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center mb-8 sm:mb-10">
              Results from <span className="text-primary">real campaigns</span>
            </h2>
          </BlurIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <ScaleUp key={t.name} delay={i * 120}>
                <div className="glass-card rounded-xl p-5 sm:p-6 h-full flex flex-col hover:scale-[1.02] hover:shadow-xl transition-all duration-300">
                  {/* KPI */}
                  <div className="mb-4 pb-3 border-b border-base-300/20">
                    <p className="text-2xl font-extrabold tracking-tight text-gradient">
                      {t.kpi}
                    </p>
                    <p className="text-xs text-base-content/40 mt-0.5">
                      {t.kpiContext}
                    </p>
                  </div>

                  <p className="text-sm text-base-content/55 leading-relaxed flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </p>

                  <div className="flex items-center gap-3 pt-4 mt-4 border-t border-base-300/20">
                    <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-xs font-bold text-primary">
                      {t.initials}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-base-content/70">
                        {t.name}
                      </p>
                      <p className="text-xs text-base-content/35">
                        {t.role}, {t.company}
                      </p>
                    </div>
                  </div>
                </div>
              </ScaleUp>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            8. PRICING — Clear, honest
            ═══════════════════════════════════════════ */}
        <section
          id="pricing"
          className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20"
        >
          <BlurIn>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center mb-2">
              Simple,{' '}
              <span className="text-gradient">transparent pricing</span>
            </h2>
            <p className="text-center text-sm text-base-content/40 mb-10">
              Try it free. Upgrade when you see results.
            </p>
          </BlurIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto">
            {/* Free */}
            <SlideInLeft>
              <div className="glass-card rounded-xl p-6 sm:p-7 h-full flex flex-col hover:shadow-xl transition-all duration-300">
                <div className="mb-5">
                  <span className="badge badge-outline badge-sm mb-3">
                    Free
                  </span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold">
                      <CountUp prefix="€" end={0} duration={800} />
                    </span>
                  </div>
                  <p className="text-sm text-base-content/40 mt-1.5">
                    One-time credit — no expiry
                  </p>
                </div>
                <StaggerChildren className="space-y-2.5 flex-1" staggerMs={80}>
                  {freePlanFeatures.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle
                        size={15}
                        className="text-success shrink-0 mt-0.5"
                      />
                      <span className="text-base-content/55">{f}</span>
                    </div>
                  ))}
                </StaggerChildren>
                <Link
                  to="/onboarding"
                  className="btn btn-ghost btn-block mt-7 border-base-300/30 min-h-[44px]"
                >
                  Start free
                </Link>
              </div>
            </SlideInLeft>

            {/* Pro */}
            <SlideInRight delay={150}>
              <div className="glass-card rounded-xl p-6 sm:p-7 h-full flex flex-col ring-2 ring-primary/30 hover:shadow-xl hover:ring-primary/50 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-content text-[10px] font-bold px-2.5 py-1 rounded-bl-lg uppercase tracking-wider">
                  Popular
                </div>
                <div className="mb-5">
                  <span className="badge badge-primary badge-sm mb-3">Pro</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold">
                      <CountUp prefix="€" end={49} duration={1200} />
                    </span>
                    <span className="text-sm text-base-content/40">/month</span>
                  </div>
                  <p className="text-sm text-base-content/40 mt-1.5">
                    5,000 tokens every month
                  </p>
                </div>
                <StaggerChildren className="space-y-2.5 flex-1" staggerMs={80}>
                  {proPlanFeatures.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle
                        size={15}
                        className="text-primary shrink-0 mt-0.5"
                      />
                      <span className="text-base-content/55">{f}</span>
                    </div>
                  ))}
                </StaggerChildren>
                <Link
                  to="/onboarding"
                  className="btn btn-primary btn-block mt-7 min-h-[44px]"
                >
                  Start closing deals — €49/mo
                </Link>
              </div>
            </SlideInRight>
          </div>
          <p className="text-center text-xs text-base-content/25 mt-5">
            ~15 tokens = 1 personalized email &middot; ~30 tokens = 1 lead
            researched &middot; 100 tokens = €1
          </p>
        </section>

        {/* ═══════════════════════════════════════════
            9. INTEGRATIONS — For power users
            ═══════════════════════════════════════════ */}
        <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20">
          <FadeUp>
            <div className="text-center space-y-2 mb-8 sm:mb-10">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                Use HyPeMa{' '}
                <span className="text-gradient">from anywhere</span>
              </h2>
              <p className="text-sm text-base-content/40 max-w-md mx-auto">
                Web app, AI assistant, or API — your workflow, your choice.
              </p>
            </div>
          </FadeUp>
          <StaggerChildren
            className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5"
            staggerMs={120}
          >
            {[
              {
                icon: Chrome,
                title: 'Chrome Extension',
                desc: 'Creates Gmail drafts in your real inbox. Review, tweak, send — your domain, your reputation.',
                code: null,
              },
              {
                icon: Bot,
                title: 'MCP Server',
                desc: 'Connect to Claude, ChatGPT, Cursor, or any MCP-compatible AI assistant.',
                code: 'claude mcp add hypema --transport http https://mcp.hypema.app/mcp',
              },
              {
                icon: Code2,
                title: 'REST API',
                desc: 'Build custom workflows. Research, generate, send — all programmatic.',
                code: 'curl https://hypema.app/api/v1/leads -H "Authorization: Bearer hpm_live_..."',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="glass-card rounded-xl p-5 sm:p-6 space-y-3 hover:scale-[1.02] hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <item.icon
                    size={20}
                    className="text-primary group-hover:scale-110 transition-transform"
                  />
                </div>
                <h3 className="text-base font-semibold">{item.title}</h3>
                <p className="text-sm text-base-content/50 leading-relaxed">
                  {item.desc}
                </p>
                {item.code && (
                  <div className="bg-base-300/20 rounded-lg px-3 py-2 overflow-x-auto">
                    <code className="text-[11px] text-primary/60 font-mono whitespace-nowrap">
                      {item.code}
                    </code>
                  </div>
                )}
              </div>
            ))}
          </StaggerChildren>
        </section>

        {/* ═══════════════════════════════════════════
            10. FAQ — Handle objections
            ═══════════════════════════════════════════ */}
        <section
          id="faq"
          className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20"
        >
          <BlurIn>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center mb-8 sm:mb-10">
              Questions
            </h2>
          </BlurIn>
          <StaggerChildren className="space-y-2.5" staggerMs={60}>
            {faqs.map((faq) => (
              <details
                key={faq.q}
                className="collapse collapse-arrow glass-card rounded-xl"
              >
                <summary className="collapse-title text-sm font-semibold pr-10 min-h-[48px]">
                  {faq.q}
                </summary>
                <div className="collapse-content">
                  <p className="text-sm text-base-content/50 leading-relaxed pt-0">
                    {faq.a}
                  </p>
                </div>
              </details>
            ))}
          </StaggerChildren>
        </section>

        {/* ═══════════════════════════════════════════
            11. FINAL CTA — Close
            ═══════════════════════════════════════════ */}
        <section className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20 text-center">
          <ScaleUp>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight mb-3"
              style={{ textWrap: 'balance' } as React.CSSProperties}
            >
              Stop sending emails{' '}
              <span className="text-gradient">nobody reads.</span>
            </h2>
            <p className="text-base-content/40 mb-6 text-sm sm:text-base">
              500 free tokens. No credit card. No warmup. No spam infrastructure.
            </p>
            <Link
              to="/onboarding"
              className="btn btn-primary btn-lg gap-2 min-h-[48px]"
            >
              Send your first campaign free <ArrowRight size={18} />
            </Link>
          </ScaleUp>
        </section>

        {/* ═══════════════════════════════════════════
            FOOTER
            ═══════════════════════════════════════════ */}
        <footer className="relative z-10 border-t border-base-300/30 py-10 sm:py-12 px-5 sm:px-6 lg:px-12">
          <FadeUp>
            <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="col-span-2 md:col-span-1 space-y-2">
                <div className="text-xl font-extrabold tracking-tight">
                  Hy<span className="text-primary">Pe</span>Ma
                </div>
                <p className="text-xs text-base-content/35 leading-relaxed max-w-xs">
                  Hyper-personalized cold email outreach powered by AI research.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/30">
                  Product
                </h4>
                <div className="flex flex-col gap-1.5">
                  <a href="#how-it-works" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">How it works</a>
                  <a href="#pricing" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Pricing</a>
                  <a href="#faq" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">FAQ</a>
                  <a href={PLACEHOLDER_CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Chrome Extension</a>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/30">
                  Developers
                </h4>
                <div className="flex flex-col gap-1.5">
                  <Link to="/docs/guide" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Academy</Link>
                  <Link to="/docs/api-reference" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">API Reference</Link>
                  <Link to="/docs/mcp" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">MCP Integration</Link>
                  <a href="/llms.txt" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">llms.txt</a>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/30">
                  Legal
                </h4>
                <div className="flex flex-col gap-1.5">
                  <Link to="/privacy" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Privacy</Link>
                  <Link to="/terms" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Terms</Link>
                  <Link to="/imprint" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Imprint</Link>
                  <Link to="/cookies" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Cookies</Link>
                  <a href="mailto:service@newstelligent.com" className="text-sm text-base-content/45 hover:text-base-content transition-colors w-fit">Contact</a>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto mt-8 pt-5 border-t border-base-300/15 flex flex-wrap items-center justify-between gap-4 text-xs text-base-content/20">
              <span>&copy; {new Date().getFullYear()} HyPeMa</span>
              <span>Precision over volume.</span>
            </div>
          </FadeUp>
        </footer>
      </div>
    </VersionToggle>
  );
}
